<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CATEGORIES, type Family } from '../model/catalog'
import { familiesByCategory } from '../model/ranking'

/**
 * Which instance families the list shows, grouped by what they are for. Collapsed
 * by default: the defaults are a sensible start, and a wall of checkboxes is not.
 */
const props = defineProps<{ families: readonly Family[]; selected: ReadonlySet<string> }>()
const emit = defineEmits<{ update: [string[]] }>()

const { t } = useI18n()

const groups = computed(() => {
  const grouped = familiesByCategory(props.families)
  return CATEGORIES.flatMap((category) => {
    const families = grouped.get(category)
    return families ? [{ category, families }] : []
  })
})

function toggle(id: string, on: boolean): void {
  const next = new Set(props.selected)
  if (on) next.add(id)
  else next.delete(id)
  emit('update', [...next])
}

function setCategory(families: readonly Family[], on: boolean): void {
  const next = new Set(props.selected)
  for (const family of families) {
    if (on) next.add(family.id)
    else next.delete(family.id)
  }
  emit('update', [...next])
}

const defaults = (): void =>
  emit(
    'update',
    props.families.filter((f) => f.default).map((f) => f.id),
  )

const checkedIn = (families: readonly Family[]): number =>
  families.filter((f) => props.selected.has(f.id)).length
</script>

<template>
  <details class="filter">
    <summary class="filter__summary">
      {{ t('list.families', { selected: selected.size, total: families.length }) }}
    </summary>
    <div class="filter__body">
      <div class="filter__actions">
        <button type="button" @click="defaults()">{{ t('list.defaults') }}</button>
        <button
          type="button"
          @click="
            emit(
              'update',
              families.map((f) => f.id),
            )
          "
        >
          {{ t('list.all') }}
        </button>
        <button type="button" @click="emit('update', [])">{{ t('list.none') }}</button>
      </div>
      <fieldset v-for="group in groups" :key="group.category" class="filter__group">
        <legend class="filter__legend">
          <label>
            <input
              type="checkbox"
              :checked="checkedIn(group.families) === group.families.length"
              :indeterminate="
                checkedIn(group.families) > 0 && checkedIn(group.families) < group.families.length
              "
              @change="setCategory(group.families, ($event.target as HTMLInputElement).checked)"
            />
            {{ t(`category.${group.category}`) }}
          </label>
        </legend>
        <div class="filter__families">
          <label v-for="family in group.families" :key="family.id" class="filter__family">
            <input
              type="checkbox"
              :checked="selected.has(family.id)"
              @change="toggle(family.id, ($event.target as HTMLInputElement).checked)"
            />
            <span class="mono">{{ family.id }}</span>
          </label>
        </div>
      </fieldset>
    </div>
  </details>
</template>

<style scoped>
.filter {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.filter__summary {
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  cursor: pointer;
}

.filter__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-height: 22rem;
  padding: 0 var(--space-3) var(--space-3);
  overflow-y: auto;
}

.filter__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.filter__actions button {
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.filter__group {
  margin: 0;
  padding: 0;
  border: 0;
}

.filter__legend {
  padding: 0;
  font-size: var(--text-sm);
  font-weight: 600;
}

.filter__families {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr));
  gap: var(--space-1) var(--space-3);
  margin-top: var(--space-1);
}

.filter__family {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
}

label {
  cursor: pointer;
}
</style>
